/**
 * Single HTTP client for the whole SPA: public pages, owner dashboard, and Front Desk
 * all use this module + the same base URL rules (no separate “staff API”).
 *
 * Resolution order:
 * 1. REACT_APP_API_URL (set in CI / .env at build time)
 * 2. window.__LUXISTT_API_BASE__ (optional runtime override; set in a script before the bundle)
 * 3. If the page is served from *.luxistt.com → canonical production API (covers missing CI env)
 * 4. Local dev default: 127.0.0.1 (not "localhost", avoids IPv6 ::1 vs artisan IPv4)
 */
const CANONICAL_PRODUCTION_API = 'https://api.luxistt.com/api/v1';
const LOCAL_DEFAULT_API = 'http://127.0.0.1:8000/api/v1';

function stripTrailingSlashes(s) {
  return s.replace(/\/+$/, '');
}

function isLuxisttProductionHost(hostname) {
  if (!hostname) return false;
  return hostname === 'luxistt.com' || hostname === 'www.luxistt.com' || hostname.endsWith('.luxistt.com');
}

function normalizeApiBase(raw) {
  if (!raw) return '';
  let value = stripTrailingSlashes(String(raw).trim());
  // Common typo seen in deploy envs: luxisitt -> luxistt.
  value = value.replace(/:\/\/api\.luxisitt\.com\b/i, '://api.luxistt.com');
  return value;
}

function looksLikeLuxisttApi(base) {
  try {
    const u = new URL(base);
    return u.hostname === 'api.luxistt.com';
  } catch {
    return false;
  }
}

/** Shown when fetch fails (offline, DNS, blocked request, etc.). Avoids exposing URLs or dev jargon. */
export const NETWORK_UNAVAILABLE_USER_MESSAGE =
  "We're having trouble connecting right now. Please check your internet connection and try again. If the problem continues, wait a few minutes and try again.";

const NETWORK_UNAVAILABLE_LOCAL_USER_MESSAGE =
  "We couldn't reach the API server. If you're working on this project locally, start the API and try again.";

/**
 * True when the failure is almost certainly transport-layer (no HTTP response), not an application error.
 * @param {unknown} err
 */
export function isBrowserFetchNetworkError(err) {
  if (err == null || typeof err !== 'object') {
    return false;
  }
  if (err.name === 'TypeError') {
    return true;
  }
  const m = String(/** @type {{ message?: string }} */ (err).message || '');
  return (
    m === 'Failed to fetch'
    || m === 'NetworkError when attempting to fetch resource.'
    || m === 'Load failed'
    || m.startsWith('NetworkError')
  );
}

function apiBaseLooksLocal(base) {
  return /127\.0\.0\.1|localhost/i.test(base);
}

/**
 * Rethrow with a user-safe message when the browser could not complete the request.
 * @param {unknown} err
 * @param {{ url?: string, localApi?: boolean }} [meta]
 */
export function rethrowAsUserFriendlyNetworkError(err, meta = {}) {
  if (!isBrowserFetchNetworkError(err)) {
    if (err instanceof Error) {
      throw err;
    }
    throw new Error(String(/** @type {{ message?: string }} */ (err)?.message || err));
  }
  if (typeof process !== 'undefined' && process.env.NODE_ENV === 'development' && typeof console !== 'undefined') {
    console.warn('[Luxistt] Request could not reach the server (offline, DNS, or blocked).', meta.url || '', err);
  }
  const local = meta.localApi ?? apiBaseLooksLocal(getBaseUrl());
  throw new Error(local ? NETWORK_UNAVAILABLE_LOCAL_USER_MESSAGE : NETWORK_UNAVAILABLE_USER_MESSAGE);
}

const getBaseUrl = () => {
  const isProdSite = typeof window !== 'undefined' && isLuxisttProductionHost(window.location.hostname);
  const fromEnv = (process.env.REACT_APP_API_URL || '').trim();
  if (fromEnv) {
    const normalized = normalizeApiBase(fromEnv);
    if (isProdSite && !looksLikeLuxisttApi(normalized)) return CANONICAL_PRODUCTION_API;
    return normalized;
  }
  if (typeof window !== 'undefined' && window.__LUXISTT_API_BASE__) {
    const w = String(window.__LUXISTT_API_BASE__).trim();
    if (w) {
      const normalized = normalizeApiBase(w);
      if (isProdSite && !looksLikeLuxisttApi(normalized)) return CANONICAL_PRODUCTION_API;
      return normalized;
    }
  }
  if (isProdSite) {
    return CANONICAL_PRODUCTION_API;
  }
  return LOCAL_DEFAULT_API;
};

/**
 * Get stored token (from localStorage).
 */
export const getStoredToken = () => localStorage.getItem('luxistt_token');

/**
 * Build headers for API requests (JSON + optional Bearer token).
 */
export function apiHeaders(token = getStoredToken()) {
  const headers = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  };
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

/**
 * Run a request and parse JSON. Throws on non-ok response with body message if any.
 */
async function request(path, options = {}) {
  const url = `${getBaseUrl()}${path.startsWith('/') ? path : `/${path}`}`;
  // `token: undefined` (omit) → use stored Bearer token. `token: null` → no auth (login/register/public).
  // Do not pass `null` for authenticated routes — that drops the token and causes 401 Unauthenticated.
  const authToken = options.token === undefined ? getStoredToken() : options.token;
  const headers = { ...apiHeaders(authToken), ...options.headers };
  if (options.body instanceof FormData) {
    delete headers['Content-Type'];
  }
  const body = options.body != null && typeof options.body === 'object' && !(options.body instanceof FormData)
    ? JSON.stringify(options.body)
    : options.body;
  let res;
  try {
    res = await fetch(url, { ...options, headers, body });
  } catch (err) {
    rethrowAsUserFriendlyNetworkError(err, { url });
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg = data.message || data.error || res.statusText;
    const err = new Error(msg);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

/**
 * Laravel JSON validation body: `{ message?: string, errors?: Record<string, string[] | string> }`.
 * @param {unknown} data Parsed response body from a failed request.
 * @returns {string[]} Human-readable messages (empty if none found).
 */
export function validationMessagesFromApiPayload(data) {
  if (!data || typeof data !== 'object') return [];
  const raw = /** @type {{ errors?: unknown }} */ (data).errors;
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    const msgs = [];
    for (const v of Object.values(raw)) {
      if (Array.isArray(v)) {
        for (const item of v) {
          if (item != null && String(item).trim() !== '') msgs.push(String(item));
        }
      } else if (v != null && String(v).trim() !== '') {
        msgs.push(String(v));
      }
    }
    if (msgs.length > 0) return msgs;
  }
  const message = /** @type {{ message?: unknown }} */ (data).message;
  if (message != null && String(message).trim() !== '') {
    return [String(message)];
  }
  return [];
}

/**
 * First validation message per Laravel field key (for inline label errors).
 * @param {unknown} data Parsed JSON body (422).
 * @returns {Record<string, string>}
 */
export function firstFieldMessagesFromApiPayload(data) {
  /** @type {Record<string, string>} */
  const out = {};
  if (!data || typeof data !== 'object') return out;
  const raw = /** @type {{ errors?: unknown }} */ (data).errors;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return out;
  for (const [key, val] of Object.entries(raw)) {
    const arr = Array.isArray(val) ? val : [val];
    const msg = arr.find((x) => x != null && String(x).trim() !== '');
    if (msg != null) out[key] = String(msg);
  }
  return out;
}

/**
 * @param {unknown} err Thrown from {@link request} (has optional `.data` and `.message`).
 * @param {string} [fallback]
 * @returns {string}
 */
export function formatApiErrorUserMessage(err, fallback = 'Something went wrong. Please try again.') {
  const msgs = validationMessagesFromApiPayload(
    err && typeof err === 'object' && 'data' in err ? /** @type {{ data?: unknown }} */ (err).data : null,
  );
  if (msgs.length > 0) return msgs.join(' ');
  const m = err && typeof err === 'object' && 'message' in err ? /** @type {{ message?: unknown }} */ (err).message : null;
  if (m != null && String(m).trim() !== '') return String(m);
  return fallback;
}

export const api = {
  /** @param {string} path @param {string|null|undefined} token @param {RequestInit} [init] extra fetch options (e.g. signal) */
  get: (path, token, init = {}) => request(path, { method: 'GET', token, ...init }),
  /** POST multipart (FormData); do not set Content-Type manually — boundary is set by the browser. */
  post: (path, body, token) => request(path, { method: 'POST', body, token }),
  put: (path, body, token) => request(path, { method: 'PUT', body, token }),
  patch: (path, body, token) => request(path, { method: 'PATCH', body, token }),
  delete: (path, token) => request(path, { method: 'DELETE', token }),
};

/**
 * Email/password login; retries with `client: front-desk` when API returns use_front_desk (owners / staff without guest role).
 * @param {string} email
 * @param {string} password
 * @param {{ client?: 'front-desk' }} [opts]
 */
export async function postAuthLogin(email, password, opts = {}) {
  const body = { email, password, ...opts };
  try {
    return await api.post('/auth/login', body, null);
  } catch (e) {
    if (e.status === 403 && e.data?.use_front_desk && !opts.client) {
      return await api.post('/auth/login', { ...body, client: 'front-desk' }, null);
    }
    throw e;
  }
}

/** {@link PdfViewerHost} listens for this on `window` to show PDFs in-page (no new window). */
export const LUXISTT_SHOW_PDF_EVENT = 'luxistt:show-pdf';

/**
 * Show a PDF in the in-app viewer (full-screen iframe). Does not use `window.open` or new tabs.
 * Falls back to a file download only if the custom event cannot be dispatched.
 *
 * @param {Blob} blob
 * @param {{ filename?: string }} [options] Suggested filename for download fallback (.pdf added if missing)
 * @returns {'viewer' | 'download'} how the PDF was delivered
 */
export function openPdfBlobInNewTab(blob, options = {}) {
  let filename = (options.filename && String(options.filename).trim()) || 'luxistt-document.pdf';
  if (!/\.pdf$/i.test(filename)) {
    filename = `${filename.replace(/\.+$/, '')}.pdf`;
  }

  const pdfBlob =
    blob && typeof blob.type === 'string' && blob.type.includes('pdf')
      ? blob
      : new Blob([blob], { type: 'application/pdf' });

  try {
    if (
      typeof window === 'undefined'
      || typeof window.dispatchEvent !== 'function'
      || typeof CustomEvent === 'undefined'
    ) {
      throw new Error('unavailable');
    }
    window.dispatchEvent(
      new CustomEvent(LUXISTT_SHOW_PDF_EVENT, { detail: { blob: pdfBlob, filename } }),
    );
    return 'viewer';
  } catch {
    const url = URL.createObjectURL(pdfBlob);
    try {
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.rel = 'noopener noreferrer';
      document.body.appendChild(a);
      a.click();
      a.remove();
    } finally {
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    }
    return 'download';
  }
}

/**
 * Fetch PDF receipt for staff (front desk). Paid bookings only. Returns a Blob; use {@link openPdfBlobInNewTab} with a filename.
 */
export async function downloadFrontDeskReceipt(bookingId) {
  const path = `/front-desk/bookings/${bookingId}/receipt`;
  const url = `${getBaseUrl()}${path}`;
  const token = getStoredToken();
  if (!token) {
    throw new Error('Please sign in to download this receipt.');
  }
  let res;
  try {
    res = await fetch(url, {
      method: 'GET',
      headers: {
        Accept: 'application/pdf',
        Authorization: `Bearer ${token}`,
      },
    });
  } catch (err) {
    rethrowAsUserFriendlyNetworkError(err, { url });
  }
  const contentType = res.headers.get('Content-Type') || '';
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    const err = new Error(data.message || res.statusText || 'Could not download receipt');
    err.status = res.status;
    throw err;
  }
  if (!contentType.includes('pdf')) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'We could not open that document. Please try again.');
  }
  return res.blob();
}

/**
 * Fetch payroll slip PDF (owner). Returns a Blob; pass to {@link openPdfBlobInNewTab}.
 */
export async function downloadOwnerPayrollSlip(payrollEntryId) {
  const path = `/owner/payroll-entries/${payrollEntryId}/slip`;
  const url = `${getBaseUrl()}${path}`;
  const token = getStoredToken();
  if (!token) {
    throw new Error('Please sign in to download this payslip.');
  }
  let res;
  try {
    res = await fetch(url, {
      method: 'GET',
      headers: {
        Accept: 'application/pdf',
        Authorization: `Bearer ${token}`,
      },
    });
  } catch (err) {
    rethrowAsUserFriendlyNetworkError(err, { url });
  }
  const contentType = res.headers.get('Content-Type') || '';
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    const err = new Error(data.message || res.statusText || 'Could not download payslip');
    err.status = res.status;
    throw err;
  }
  if (!contentType.includes('pdf')) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'We could not open that document. Please try again.');
  }
  return res.blob();
}

/**
 * Fetch guest PDF receipt (paid bookings). Returns a Blob; use {@link openPdfBlobInNewTab}.
 */
export async function downloadBookingReceipt(bookingId) {
  const path = `/user/bookings/${bookingId}/receipt`;
  const url = `${getBaseUrl()}${path}`;
  const token = getStoredToken();
  if (!token) {
    throw new Error('Please sign in to download your receipt.');
  }
  let res;
  try {
    res = await fetch(url, {
      method: 'GET',
      headers: {
        Accept: 'application/pdf',
        Authorization: `Bearer ${token}`,
      },
    });
  } catch (err) {
    rethrowAsUserFriendlyNetworkError(err, { url });
  }
  const contentType = res.headers.get('Content-Type') || '';
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    const err = new Error(data.message || res.statusText || 'Could not download receipt');
    err.status = res.status;
    throw err;
  }
  if (!contentType.includes('pdf')) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'We could not open that document. Please try again.');
  }
  return res.blob();
}

export { getBaseUrl };

export default api;
